/**
 * Which bot folders are open in the chat list.
 *
 * Per person and per device, in localStorage, for the reason the smiley bar and
 * the theme are there: it is a preference rather than content, and moving it
 * onto the account later means changing `load` and `persist` and nothing else.
 *
 * A set of names, not of ids, because that is what a folder is — see the
 * `group_name` column in server/src/db.ts. Renaming a folder in the admin panel
 * therefore closes it again on every phone, which is the right amount of
 * ceremony for a row that opens with one tap.
 */
import { useSyncExternalStore } from 'react';

const KEY = 'botFolders.v1';

/** Exported for the test: anything unreadable is «nothing is open». */
export function parseBotFolders(raw: string | null): string[] {
  if (raw === null) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((name): name is string => typeof name === 'string');
  } catch {
    return [];
  }
}

function load(): Set<string> {
  try {
    return new Set(parseBotFolders(localStorage.getItem(KEY)));
  } catch {
    // Private mode, or a store the browser has disabled.
    return new Set();
  }
}

function persist(open: Set<string>): void {
  try {
    localStorage.setItem(KEY, JSON.stringify([...open]));
  } catch {
    // The folder still opens for this session; it just will not survive a
    // reload, which is not worth telling anybody about.
  }
}

let current = load();
const listeners = new Set<() => void>();

/** Stable between changes, which is what useSyncExternalStore requires. */
export function openFolders(): Set<string> {
  return current;
}

export function toggleFolder(name: string): void {
  const next = new Set(current);
  if (!next.delete(name)) next.add(name);
  current = next;
  persist(current);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The open folders, re-rendering the list the moment one is tapped. */
export function useOpenFolders(): Set<string> {
  return useSyncExternalStore(subscribe, openFolders, openFolders);
}
